import ThinkingRadar from "@/components/ThinkingRadar";
import { ThemeColors, useColors } from "@/constants/theme";
import { getCareerThemes } from "@/lib/cardInsights";
import { isSupabaseConfigured } from "@/lib/supabase";
import { useAppStore } from "@/stores/useStore";
import { Link } from "expo-router";
import { useMemo } from "react";
import { Alert, Pressable, ScrollView, StyleSheet, View } from "react-native";
import { Button, Card, ProgressBar, Text } from "react-native-paper";

export default function MyLensScreen() {
  const {
    posts,
    currentUser,
    login,
    getCardUsageStats,
    cardUsageRecords,
    cards,
    subjects,
  } = useAppStore();
  const c = useColors();
  const styles = useMemo(() => createStyles(c), [c]);
  const myPosts = posts.filter(
    (p) => p.user_id === currentUser?.id && p.status !== "deleted",
  );
  const stats = getCardUsageStats();

  // 科目別の使用統計
  const subjectStats: { [key: string]: number } = {};
  stats.forEach((stat) => {
    const card = cards.find((c) => c.id === stat.card_id);
    if (card) {
      subjectStats[card.subject_id] =
        (subjectStats[card.subject_id] || 0) + stat.count;
    }
  });

  // トップ5カード
  const topCards = stats.slice(0, 5);
  const usedCardIds = new Set(stats.map((stat) => stat.card_id));
  const unusedCards = cards.filter((card) => !usedCardIds.has(card.id)).slice(
    0,
    3,
  );
  const crossSubjectPosts = myPosts.filter((post) => {
    const subjectIds = post.card_ids
      .map((cardId) => cards.find((card) => card.id === cardId)?.subject_id)
      .filter(Boolean);
    return new Set(subjectIds).size >= 2;
  }).length;
  const recentlyUsedCards = [...cardUsageRecords]
    .filter((record) => record.user_id === currentUser?.id)
    .sort((a, b) => b.last_used_at.localeCompare(a.last_used_at))
    .slice(0, 3);

  // 思考タイプの集計
  const thinkingTypeStats: { [key: string]: number } = {};
  stats.forEach((stat) => {
    const card = cards.find((c) => c.id === stat.card_id);
    if (card) {
      card.thinking_tags.forEach((tag) => {
        thinkingTypeStats[tag] = (thinkingTypeStats[tag] || 0) + stat.count;
      });
    }
  });

  const topThinkingTypes = Object.entries(thinkingTypeStats)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // 職業・探究テーマの集計（使ったカードの career_tags を重みづけ）
  const careerStats: { [key: string]: number } = {};
  stats.forEach((stat) => {
    const card = cards.find((c) => c.id === stat.card_id);
    getCareerThemes(card, subjects).forEach((tag) => {
      careerStats[tag] = (careerStats[tag] || 0) + stat.count;
    });
  });
  const topCareers = Object.entries(careerStats)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  // 全体の達成率（30投稿を目標とする）
  const targetPosts = 30;
  const achievementRate = Math.min(myPosts.length / targetPosts, 1);
  const cardCatalogSize = cards.length;
  const cardCatalogRate = Math.min(usedCardIds.size / cardCatalogSize, 1);

  if (!currentUser) {
    return (
      <View style={styles.loginContainer}>
        <Text variant="headlineSmall" style={styles.loginTitle}>
          マイレンズはログイン後に表示されます
        </Text>
        {isSupabaseConfigured ? (
          <Link href="/profile" asChild>
            <Pressable accessibilityRole="button" style={styles.loginLinkButton}>
              <Text style={styles.loginLinkText}>ログイン画面へ</Text>
            </Pressable>
          </Link>
        ) : (
          <Button
            mode="contained"
            onPress={() => {
              login().catch((error) => {
                console.warn("Failed to use demo login", error);
                Alert.alert("ログインできませんでした", "もう一度お試しください。");
              });
            }}
            style={styles.loginButton}
          >
            体験ログイン
          </Button>
        )}
      </View>
    );
  }

  return (
    <ScrollView style={styles.container}>
      {/* ヘッダー */}
      <View style={styles.header}>
        <Text variant="headlineLarge" style={styles.title}>
          🔍 マイレンズ
        </Text>
        <Text variant="bodyMedium" style={styles.subtitle}>
          あなたの思考傾向を可視化
        </Text>
      </View>

      {/* アクティビティカード */}
      <Card style={styles.card} mode="elevated">
        <Card.Content>
          <View style={styles.statContainer}>
            <View>
              <Text variant="bodySmall" style={styles.statLabel}>
                📝 総発見数（投稿）
              </Text>
              <Text variant="headlineSmall" style={styles.statValue}>
                {myPosts.length}
              </Text>
            </View>
            <View>
              <Text variant="bodySmall" style={styles.statLabel}>
                🎯 使ったカード
              </Text>
              <Text variant="headlineSmall" style={styles.statValue}>
                {usedCardIds.size}
              </Text>
            </View>
            <View>
              <Text variant="bodySmall" style={styles.statLabel}>
                ⚡ 総使用数
              </Text>
              <Text variant="headlineSmall" style={styles.statValue}>
                {stats.reduce((sum, s) => sum + s.count, 0)}
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* 達成率 */}
      <Card style={styles.card} mode="elevated">
        <Card.Title title="📊 活用の達成率" />
        <Card.Content>
          <Text variant="bodySmall" style={styles.description}>
            目標：1ヶ月に30の発見を記録
          </Text>
          <View style={styles.progressContainer}>
            <ProgressBar
              progress={achievementRate}
              color={c.primary}
              style={styles.progressBar}
            />
            <Text variant="bodySmall" style={styles.progressText}>
              {Math.round(achievementRate * 100)}%
            </Text>
          </View>
          <Text variant="bodySmall" style={styles.progressHint}>
            {targetPosts - myPosts.length > 0
              ? `あと${targetPosts - myPosts.length}個で目標達成！`
              : "🎉 目標達成おめでとう！"}
          </Text>
          <View style={styles.catalogProgress}>
            <Text variant="bodySmall" style={styles.description}>
              学問レンズ図鑑：{usedCardIds.size} / {cardCatalogSize}
            </Text>
            <ProgressBar
              progress={cardCatalogRate}
              color={c.success}
              style={styles.progressBar}
            />
          </View>
        </Card.Content>
      </Card>

      <Card style={styles.card} mode="elevated">
        <Card.Title title="🌉 教科横断の記録" />
        <Card.Content>
          <View style={styles.statContainer}>
            <View>
              <Text variant="bodySmall" style={styles.statLabel}>
                複数カード投稿
              </Text>
              <Text variant="headlineSmall" style={styles.statValue}>
                {myPosts.filter((post) => post.card_ids.length >= 2).length}
              </Text>
            </View>
            <View>
              <Text variant="bodySmall" style={styles.statLabel}>
                教科横断投稿
              </Text>
              <Text variant="headlineSmall" style={styles.statValue}>
                {crossSubjectPosts}
              </Text>
            </View>
            <View>
              <Text variant="bodySmall" style={styles.statLabel}>
                使用科目
              </Text>
              <Text variant="headlineSmall" style={styles.statValue}>
                {Object.keys(subjectStats).length}
              </Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      {/* 科目別使用度 */}
      <Card style={styles.card} mode="elevated">
        <Card.Title title="🏫 科目別の活用度" />
        <Card.Content>
          {Object.keys(subjectStats).length > 0 ? (
            <View>
              {Object.entries(subjectStats)
                .sort((a, b) => b[1] - a[1])
                .map(([subjectId, count]) => {
                  const subject = subjects.find((s) => s.id === subjectId);
                  const maxCount = Math.max(...Object.values(subjectStats));
                  const percentage = count / maxCount;
                  return (
                    <View key={subjectId} style={styles.statBar}>
                      <View style={styles.statBarLabel}>
                        <Text style={styles.statBarLabelText}>
                          {subject?.name}
                        </Text>
                        <Text style={styles.statBarCount}>{count}回</Text>
                      </View>
                      <View style={styles.barContainer}>
                        <View
                          style={[
                            styles.bar,
                            {
                              width: `${percentage * 100}%`,
                              backgroundColor: subject?.color_code || c.border,
                            },
                          ]}
                        />
                      </View>
                    </View>
                  );
                })}
            </View>
          ) : (
            <Text style={styles.emptyText}>まだデータがありません</Text>
          )}
        </Card.Content>
      </Card>

      {/* よく使うレンズ */}
      {topCards.length > 0 && (
        <Card style={styles.card} mode="elevated">
          <Card.Title title="⭐ よく使うレンズトップ5" />
          <Card.Content>
            {topCards.map((stat, index) => {
              const card = cards.find((c) => c.id === stat.card_id);
              const subject = subjects.find((s) => s.id === card?.subject_id);
              return (
                <View key={stat.card_id} style={styles.topCardItem}>
                  <View style={styles.rankBadge}>
                    <Text style={styles.rankBadgeText}>{index + 1}</Text>
                  </View>
                  <View style={styles.topCardInfo}>
                    <Text
                      style={[
                        styles.topCardTitle,
                        { color: subject?.color_code },
                      ]}
                    >
                      {card?.title}
                    </Text>
                    <Text style={styles.topCardSubtitle}>
                      {card?.lens_name}
                    </Text>
                  </View>
                  <Text style={styles.topCardCount}>{stat.count}回</Text>
                </View>
              );
            })}
          </Card.Content>
        </Card>
      )}

      {/* 思考タイプ */}
      {topThinkingTypes.length > 0 && (
        <Card style={styles.card} mode="elevated">
          <Card.Title title="🧠 あなたの思考タイプ" />
          <Card.Content>
            {topThinkingTypes.length >= 3 && (
              <ThinkingRadar
                data={topThinkingTypes.map(([tag, count]) => ({
                  label: tag,
                  value: count,
                }))}
              />
            )}
            <Text variant="bodySmall" style={styles.description}>
              よく使う思考の傾向：
            </Text>
            <View style={styles.thinkingTypes}>
              {topThinkingTypes.map(([tag, count]) => (
                <View key={tag} style={styles.thinkingTypeItem}>
                  <Text style={styles.thinkingTypeTag}>{tag}</Text>
                  <Text style={styles.thinkingTypeCount}>{count}</Text>
                </View>
              ))}
            </View>
          </Card.Content>
        </Card>
      )}

      {/* つながる職業・探究テーマ */}
      {topCareers.length > 0 && (
        <Card style={styles.card} mode="elevated">
          <Card.Title title="🎓 つながる職業・探究テーマ" />
          <Card.Content>
            <Text variant="bodySmall" style={styles.description}>
              あなたの使ったレンズは、こんな分野や仕事の見方につながっています：
            </Text>
            <View style={styles.thinkingTypes}>
              {topCareers.map(([tag, count]) => (
                <View key={tag} style={styles.careerItem}>
                  <Text style={styles.careerTag}>{tag}</Text>
                  <Text style={styles.careerCount}>{count}</Text>
                </View>
              ))}
            </View>
          </Card.Content>
        </Card>
      )}

      {/* 思考傾向プロフィール */}
      {stats.length > 0 && (
        <Card style={styles.card} mode="elevated">
          <Card.Title title="👤 あなたの思考傾向プロフィール" />
          <Card.Content>
            <View style={styles.profileText}>
              <Text style={styles.profileContent}>
                {generateProfile(stats, cards)}
              </Text>
            </View>
          </Card.Content>
        </Card>
      )}

      {/* 次のステップ */}
      <Card style={styles.card} mode="elevated">
        <Card.Title title="🚀 次に使ってみたいレンズ" />
        <Card.Content>
          {stats.length > 0 ? (
            <View style={styles.recommendationList}>
              {unusedCards.map((card) => {
                const subject = subjects.find((s) => s.id === card.subject_id);
                return (
                  <View key={card.id} style={styles.recommendationItem}>
                    <Text
                      style={[
                        styles.recommendationTitle,
                        { color: subject?.color_code },
                      ]}
                    >
                      {card.title}
                    </Text>
                    <Text style={styles.recommendationText}>
                      {subject?.name}・{card.lens_name}
                    </Text>
                  </View>
                );
              })}
            </View>
          ) : (
            <Text style={styles.emptyText}>
              カードを使うことで、次のステップが提案されます
            </Text>
          )}
        </Card.Content>
      </Card>

      {recentlyUsedCards.length > 0 && (
        <Card style={styles.card} mode="elevated">
          <Card.Title title="🕒 最近使ったカード" />
          <Card.Content>
            {recentlyUsedCards.map((record) => {
              const card = cards.find((item) => item.id === record.card_id);
              const subject = subjects.find((s) => s.id === record.subject_id);
              return (
                <View key={record.card_id} style={styles.topCardItem}>
                  <View
                    style={[
                      styles.recentDot,
                      { backgroundColor: subject?.color_code || c.primary },
                    ]}
                  />
                  <View style={styles.topCardInfo}>
                    <Text
                      style={[
                        styles.topCardTitle,
                        { color: subject?.color_code },
                      ]}
                    >
                      {card?.title}
                    </Text>
                    <Text style={styles.topCardSubtitle}>
                      {new Date(record.last_used_at).toLocaleDateString("ja-JP")}
                    </Text>
                  </View>
                </View>
              );
            })}
          </Card.Content>
        </Card>
      )}

      <View style={{ height: 30 }} />
    </ScrollView>
  );
}

// プロフィール文生成関数
const generateProfile = (stats: any[], cards: any[]) => {
  const topTag =
    stats.length > 0
      ? cards.find((c) => c.id === stats[0].card_id)?.thinking_tags[0] ||
        "多角的思考"
      : "";

  const count = stats.reduce((sum, s) => sum + s.count, 0);

  if (count === 0)
    return "まだ発見が記録されていません。日常の中で学問レンズを使ってみてください！";

  if (topTag === "ロジック") {
    return `📊 あなたは「${topTag}」で物事を分析するタイプです。データや因果関係から世界を理解しています。この視点で複雑な問題を解決する力が強いでしょう。`;
  } else if (topTag === "イメージ" || topTag === "表現") {
    return `🎨 あなたは「${topTag}」で世界を捉えるタイプです。感情や感覚を大切にし、創造的な視点で物事を見ています。`;
  } else if (topTag === "システム") {
    return `🔗 あなたは「${topTag}」で全体像を考えるタイプです。複数の要素の関係性から世界を理解しています。`;
  } else {
    return `✨ あなたは多様な視点から世界を見つめるタイプです。${count}個の発見を通じて、独特の思考スタイルが育ちつつあります。`;
  }
};

const createStyles = (c: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: c.bg,
  },
  loginContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
    backgroundColor: c.bg,
  },
  loginTitle: {
    textAlign: "center",
    fontWeight: "700",
    marginBottom: 16,
    color: c.textPrimary,
  },
  loginButton: {
    minWidth: 160,
  },
  loginLinkButton: {
    minWidth: 160,
    minHeight: 44,
    borderRadius: 22,
    backgroundColor: c.primary,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 18,
  },
  loginLinkText: {
    color: "#fff",
    fontWeight: "700",
  },
  header: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 16,
    backgroundColor: c.surface,
    borderBottomWidth: 1,
    borderBottomColor: c.border,
  },
  title: {
    fontWeight: "700",
    marginBottom: 4,
    color: c.textPrimary,
  },
  subtitle: {
    color: c.textSecondary,
  },
  card: {
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 0,
    backgroundColor: c.surface,
  },
  statContainer: {
    flexDirection: "row",
    justifyContent: "space-around",
    paddingVertical: 16,
  },
  statLabel: {
    color: c.textSecondary,
    marginBottom: 4,
  },
  statValue: {
    fontWeight: "700",
    color: c.primary,
  },
  description: {
    color: c.textSecondary,
    marginBottom: 12,
    lineHeight: 18,
  },
  progressContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  progressBar: {
    flex: 1,
    height: 8,
    borderRadius: 4,
    backgroundColor: c.surfaceAlt,
  },
  progressText: {
    fontWeight: "700",
    minWidth: 40,
    textAlign: "right",
  },
  progressHint: {
    marginTop: 8,
    color: c.textSecondary,
  },
  catalogProgress: {
    marginTop: 18,
    gap: 8,
  },
  statBar: {
    marginBottom: 12,
  },
  statBarLabel: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  statBarLabelText: {
    fontWeight: "600",
    fontSize: 13,
    color: c.textPrimary,
  },
  statBarCount: {
    fontSize: 12,
    color: c.textMuted,
  },
  barContainer: {
    height: 6,
    backgroundColor: c.surfaceAlt,
    borderRadius: 3,
    overflow: "hidden",
  },
  bar: {
    height: "100%",
    borderRadius: 3,
  },
  topCardItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 8,
    marginBottom: 8,
    backgroundColor: c.surfaceAlt,
    borderRadius: 8,
  },
  rankBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: c.primary,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 12,
  },
  rankBadgeText: {
    color: "#fff",
    fontWeight: "700",
    fontSize: 14,
  },
  topCardInfo: {
    flex: 1,
  },
  recentDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  topCardTitle: {
    fontWeight: "700",
    marginBottom: 2,
    color: c.textPrimary,
  },
  topCardSubtitle: {
    fontSize: 11,
    color: c.textMuted,
  },
  topCardCount: {
    fontWeight: "700",
    color: c.primary,
    fontSize: 14,
  },
  thinkingTypes: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  thinkingTypeItem: {
    backgroundColor: c.primarySoft,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  thinkingTypeTag: {
    fontWeight: "700",
    color: c.primaryDark,
    fontSize: 12,
  },
  thinkingTypeCount: {
    color: c.primaryDark,
    fontWeight: "600",
    fontSize: 11,
  },
  careerItem: {
    backgroundColor: c.surfaceAlt,
    borderWidth: 1,
    borderColor: c.border,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  careerTag: {
    fontWeight: "700",
    color: c.accent,
    fontSize: 12,
  },
  careerCount: {
    color: c.accent,
    fontWeight: "600",
    fontSize: 11,
  },
  profileText: {
    padding: 12,
    backgroundColor: c.surfaceAlt,
    borderRadius: 8,
  },
  profileContent: {
    lineHeight: 22,
    color: c.textPrimary,
  },
  suggestion: {
    lineHeight: 20,
    color: c.textSecondary,
  },
  recommendationList: {
    gap: 10,
  },
  recommendationItem: {
    backgroundColor: c.surfaceAlt,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  recommendationTitle: {
    fontWeight: "700",
    marginBottom: 4,
  },
  recommendationText: {
    color: c.textSecondary,
    fontSize: 12,
  },
  emptyText: {
    textAlign: "center",
    marginVertical: 20,
    color: c.textMuted,
  },
});
